import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { UserService } from '@app/services';
import { Observable, of } from 'rxjs';
import { filter, map, take, timeout } from 'rxjs';
import { GUARD_RESOLVE_TIMEOUT_MS } from './guard-timeout';

@Injectable({
  providedIn: "root",
})
export class SuperAdminGuard  {
  private readonly _componentName = 'SuperAdminGuard';

  constructor(readonly userService: UserService, readonly router: Router) {}

  /**
   * Wait until the API claims have loaded before deciding. isSuperAdmin reads the
   * Role.SA claim from clientStorage.claims (fetched from the API, not the id token),
   * so on a fresh app load (e.g. a link opened in a new tab) it isn't reliable until
   * claimsResolved$ settles — unlike AdminGuard, which only needs the id-token claim.
   * A timeout guards against the claims load hanging indefinitely: if the check
   * never settles we fall back to a safe deny rather than a blank, stuck page.
   */
  private canAccess(): Observable<boolean> {
    return this.userService.claimsResolved$.pipe(
      filter((resolved) => resolved !== null),
      take(1),
      map(() => this.userService.isLoggedIn && this.userService.isSuperAdmin),
      timeout({ first: GUARD_RESOLVE_TIMEOUT_MS, with: () => of(false) })
    );
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canAccess();
  }
  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canAccess();
  }
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this.canAccess();
  }
}
